import type { FertilityScoreInput } from '@atilum/types';

function get(answers: Record<string, string | string[]>, key: string): string {
  const v = answers[key];
  if (Array.isArray(v)) return v[0] ?? '';
  return v ?? '';
}

function has(answers: Record<string, string | string[]>, key: string, value: string): boolean {
  const v = answers[key];
  if (Array.isArray(v)) return v.includes(value);
  return v === value;
}

export function calculateFertilityScore({ gender, answers }: FertilityScoreInput): number {
  let score = 70;

  // --- Age ---
  const age = get(answers, 'age');
  if (age === '40_plus') score -= 8;
  else if (age === '35_39') score -= 4;

  // --- Tobacco ---
  const tobacco = get(answers, 'tobacco');
  if (tobacco === 'yes_regularly') score -= 12;
  else if (tobacco === 'occasionally') score -= 5;

  // --- Alcohol ---
  const alcohol = get(answers, 'alcohol');
  if (alcohol === 'regularly') score -= 8;
  else if (alcohol === 'occasionally') score -= 3;

  // --- Sleep ---
  if (get(answers, 'sleep') === 'less_than_6') score -= 10;

  // --- BMI ---
  const bmi = get(answers, 'bmi');
  if (bmi === 'obese') score -= 10;
  else if (bmi === 'overweight') score -= 6;
  else if (bmi === 'underweight') score -= 5;

  // --- Drugs ---
  if (get(answers, 'drugs') === 'yes') score -= 8;

  // --- Toxins ---
  const toxins = get(answers, 'toxins');
  if (toxins === 'professional_exposure') score -= 6;
  else if (toxins === 'strong_household') score -= 3;

  // --- Clothes ---
  if (get(answers, 'clothes') === 'often') score -= 3;

  // --- Diet (positive) ---
  if (get(answers, 'diet') === 'balanced') score += 5;

  // --- Stress (gendered key) ---
  const stressKey = gender === 'female' ? 'f_stress' : 'm_stress';
  const stress = get(answers, stressKey);
  if (stress === 'high') score -= 8;
  else if (stress === 'moderate') score -= 3;

  // --- Exercise (gendered key, positive) ---
  const exeKey = gender === 'female' ? 'f_exe' : 'm_exe';
  const exe = get(answers, exeKey);
  if (exe === '3_to_5_per_week') score += 5;
  else if (exe === '1_to_2_per_week') score += 3;

  // --- Female-specific ---
  if (gender === 'female') {
    if (get(answers, 'f_cycles') === 'no') score -= 6;
    if (get(answers, 'f_pain') === 'yes') score -= 5;
    if (get(answers, 'f_surg') === 'yes') score -= 4;
    if (has(answers, 'f_hist', 'pcos')) score -= 6;
    if (has(answers, 'f_hist', 'thyroid')) score -= 4;
  }

  // --- Male-specific ---
  if (gender === 'male') {
    if (get(answers, 'm_sex') === 'yes') score -= 5;
    if (get(answers, 'm_surg') === 'yes') score -= 5;
    const ster = get(answers, 'm_ster');
    if (ster === 'yes_currently') score -= 10;
    else if (ster === 'yes_past') score -= 4;
    if (get(answers, 'm_sit') === 'more_than_8') score -= 4;
    if (get(answers, 'm_under') === 'yes_often') score -= 3;
  }

  // Clamp [25, 85]
  return Math.max(25, Math.min(85, score));
}
